import { cn } from "@/lib/utils";
import { DatabaseZap, Inbox, LoaderCircle, RefreshCw } from "lucide-react";

interface LoadingStateProps {
  rows?: number;
  className?: string;
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-lg bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] dark:from-slate-700 dark:via-slate-600 dark:to-slate-700",
        className,
      )}
    />
  );
}

export function LoadingState({ rows = 3, className }: LoadingStateProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={cn("h-4 rounded", i === 0 ? "w-3/4" : i % 2 === 0 ? "w-full" : "w-5/6")} />
      ))}
    </div>
  );
}

export function KpiSkeleton() {
  return (
    <div className="kpi-tile">
      <Skeleton className="h-3 w-24 mb-3" />
      <Skeleton className="h-8 w-32 mb-1" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
}

export function ErrorState({ message = "خطا در بارگذاری داده", onRetry }: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-50 text-danger-600"><DatabaseZap className="h-5 w-5" /></span>
      <p className="text-sm font-semibold text-[var(--color-text-primary)]">{message}</p>
      <p className="text-xs text-[var(--color-text-muted)]">اتصال داده را بررسی کنید و دوباره تلاش کنید.</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-tapsi-500 px-4 py-2 text-xs font-bold text-white transition hover:bg-tapsi-600 focus-visible:ring-4 focus-visible:ring-tapsi-500/20"
        >
          <RefreshCw className="h-3.5 w-3.5" /> تلاش مجدد
        </button>
      )}
    </div>
  );
}

export function EmptyState({ message = "داده‌ای موجود نیست" }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-surface-3)] text-[var(--color-text-muted)]"><Inbox className="h-5 w-5" /></span>
      <p className="text-sm font-medium text-[var(--color-text-secondary)]">{message}</p>
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-background)]">
      <div className="flex flex-col items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-card)]"><LoaderCircle className="h-6 w-6 animate-spin text-tapsi-500" /></span>
        <p className="text-xs font-medium text-[var(--color-text-muted)]">در حال آماده‌سازی فضای تحلیل…</p>
      </div>
    </div>
  );
}
